/**
 * Иллюстрации пустых состояний.
 *
 * ── Почему вектор, а не сгенерированная картинка ────────────────────────────
 *
 * Растровая иллюстрация несёт в себе запечённый фон. В продукте с двумя темами
 * это означает светлый прямоугольник посреди тёмного экрана — и чинится только
 * второй картинкой на каждую тему, то есть удвоением веса и удвоением мест,
 * где рассинхрон никто не заметит.
 *
 * Вектор здесь рисуется currentColor и фирменным токеном, поэтому сам следует
 * теме, весит сотни байт вместо сотен килобайт и не мылится на любом экране.
 *
 * ── Почему вообще иллюстрация ───────────────────────────────────────────────
 *
 * Пустой экран с серым квадратиком-иконкой читается как несработавшая
 * загрузка. Разница между «здесь пока пусто» и «здесь сломалось» — это ровно
 * то, что должно быть видно без чтения текста.
 */

import { cn } from "@/lib/utils";

interface EmptyArtProps {
  className?: string;
}

/** Переписка не выбрана. Пузырь в мягком ореоле. */
export function EmptyChatArt({ className }: EmptyArtProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden
      className={cn("text-brand", className)}
    >
      {/*
        Ореол: одна очень светлая заливка плюс два ОБВОДНЫХ кольца.
        Три залитых круга друг на друге давали сплошное пятно — на экране это
        читалось как закрашенный прямоугольник с иконкой, а не как свечение.
        Кольцо видно кольцом.
      */}
      <circle cx="80" cy="80" r="58" className="fill-current opacity-[0.06]" />
      <circle
        cx="80"
        cy="80"
        r="58"
        className="stroke-current opacity-20"
        strokeWidth="1"
      />
      <circle
        cx="80"
        cy="80"
        r="42"
        className="stroke-current opacity-25"
        strokeWidth="1"
      />

      <path
        d="M52 60a10 10 0 0 1 10-10h36a10 10 0 0 1 10 10v22a10 10 0 0 1-10 10H74l-12 12v-12h-0a10 10 0 0 1-10-10V60Z"
        className="stroke-current"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M66 68h28M66 78h18"
        className="stroke-current opacity-40"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Карточка чата не открыта. Лист с полями. */
export function EmptyCardArt({ className }: EmptyArtProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden
      className={cn("text-brand", className)}
    >
      <circle cx="80" cy="80" r="56" className="fill-current opacity-[0.06]" />
      <circle
        cx="80"
        cy="80"
        r="56"
        className="stroke-current opacity-20"
        strokeWidth="1"
      />
      <circle
        cx="80"
        cy="80"
        r="40"
        className="stroke-current opacity-25"
        strokeWidth="1"
      />

      <rect
        x="54"
        y="44"
        width="52"
        height="72"
        rx="10"
        className="stroke-current"
        strokeWidth="2.5"
      />
      <path
        d="M66 62h28M66 76h28M66 90h16"
        className="stroke-current opacity-40"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Ничего не найдено — лупа в ореоле. */
export function EmptySearchArt({ className }: EmptyArtProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden
      className={cn("text-brand", className)}
    >
      <circle cx="80" cy="80" r="56" className="fill-current opacity-[0.06]" />
      <circle
        cx="80"
        cy="80"
        r="56"
        className="stroke-current opacity-20"
        strokeWidth="1"
      />

      <circle
        cx="74"
        cy="74"
        r="22"
        className="stroke-current"
        strokeWidth="2.5"
      />
      <path
        d="M90 90l16 16"
        className="stroke-current"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
